import { t } from 'i18next';
import { uniqBy } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import { AppliedFiltersChipsCollectedData } from '~components/Filters/AppliedFiltersChips';
import { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import { EquipmentSelectedFilter } from '~constants/enums';

// internal/external were removed here in favor of the Internal/Vendor Trucks tabs (which
// send filter[internal] directly) — keeping them would let a selection combine with a tab
// into filter[internal] + filter[shared], a combination the API now rejects.
export const getVisibilityOptions = (): FilterMenuOption[] => [
  { label: t('common.dispatchable'), value: 'dispatchable' },
];

export type Filters = {
  senderCompanies?: string[];
  equipmentTypes?: string[];
  visibility?: string[];
  labels?: string[];
};

type EquipmentFilterKey = Extract<
  keyof Filters,
  'senderCompanies' | 'equipmentTypes' | 'visibility' | 'labels'
>;

type AppliedFiltersState = Record<
  EquipmentSelectedFilter,
  {
    equipmentFilterKey: EquipmentFilterKey;
    search: string;
  }
>;

export const equipmentFilterKeyBySelectedFilter: Record<
  EquipmentSelectedFilter,
  EquipmentFilterKey
> = {
  [EquipmentSelectedFilter.SENDER_COMPANIES]: 'senderCompanies',
  [EquipmentSelectedFilter.EQUIPMENT_TYPES]: 'equipmentTypes',
  [EquipmentSelectedFilter.VISIBILITY]: 'visibility',
  [EquipmentSelectedFilter.LABELS]: 'labels',
};

type FiltersAvailableOptionsState = Record<EquipmentSelectedFilter, FilterMenuOption[]>;

const getAppliedFiltersInitialState = () => {
  const initialState = Object.entries(equipmentFilterKeyBySelectedFilter).reduce(
    (obj, [filterKey, storeKey]) => {
      const key = filterKey as EquipmentSelectedFilter;

      obj[key] = {
        equipmentFilterKey: storeKey,
        search: '',
      };

      return obj;
    },
    {} as AppliedFiltersState,
  );

  return initialState;
};

const getAppliedFiltersChipsCollectedDataInitialState = (
  availableOptions: FiltersAvailableOptionsState,
) => {
  const collectedData = Object.entries(availableOptions).reduce((obj, [key, options]) => {
    obj[key as EquipmentSelectedFilter] = options;

    return obj;
  }, {} as AppliedFiltersChipsCollectedData);

  return collectedData;
};

const AVAILABLE_OPTIONS_STORAGE_KEY = 'equipment_availableOptions';
type AppliedFiltersStoredAvailableOptions = Partial<
  Record<EquipmentSelectedFilter, FilterMenuOption[]>
>;

const getAvailableOptionsInitialState = () => {
  const stored = localStorage.getItem(AVAILABLE_OPTIONS_STORAGE_KEY);
  const parsed = (
    stored ? JSON.parse(stored) : {}
  ) as AppliedFiltersStoredAvailableOptions;

  // Ensure visibility options are included if they are empty or missing in the local storage
  if (
    !parsed[EquipmentSelectedFilter.VISIBILITY] ||
    parsed[EquipmentSelectedFilter.VISIBILITY].length === 0
  ) {
    parsed[EquipmentSelectedFilter.VISIBILITY] = getVisibilityOptions();
  }

  const initialState = {
    ...Object.values(EquipmentSelectedFilter).reduce((obj, filter) => {
      obj[filter] = parsed[filter] ?? [];

      return obj;
    }, {} as FiltersAvailableOptionsState),
  };

  return initialState;
};

export default function useEquipmentFilters() {
  const [appliedFilters, setAppliedFilters] = useState(getAppliedFiltersInitialState());
  const [availableOptions, setAvailableOptions] = useState(
    getAvailableOptionsInitialState(),
  );

  const [appliedFilterChipsCollectedData, setAppliedFilterChipsCollectedData] = useState(
    getAppliedFiltersChipsCollectedDataInitialState(availableOptions),
  );

  const updateAppliedFilterChipsCollectedData = useCallback(
    (filter: string, data: FilterMenuOption[]) => {
      const isValidFilter =
        equipmentFilterKeyBySelectedFilter[filter as EquipmentSelectedFilter];

      if (isValidFilter) {
        setAppliedFilterChipsCollectedData((state) => ({
          ...state,
          [filter]: uniqBy([...(state[filter] ?? []), ...data], (i) => i.value),
        }));
      }
    },
    [],
  );

  useEffect(() => {
    const options = Object.entries(availableOptions).reduce((obj, [key, options]) => {
      obj[key as EquipmentSelectedFilter] = options;
      return obj;
    }, {} as AppliedFiltersStoredAvailableOptions);

    localStorage.setItem(AVAILABLE_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  }, [availableOptions]);

  return {
    availableOptions,
    updateAvailableOptions: setAvailableOptions,
    appliedFilters,
    updateAppliedFilters: setAppliedFilters,
    appliedFilterChipsCollectedData,
    updateAppliedFilterChipsCollectedData,
  };
}
